import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Search, Sparkles, ShieldCheck, ArrowRight, Zap, CheckCircle2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { SiteHeader, SiteFooter } from "@/components/tools/ToolShell";
import { PT_BR_CALCULATOR_CATEGORIES, PT_BR_CALCULATORS, type PtBrCalculatorCategory } from "@/lib/pt-br-calculators-catalog";
import { CalcIcon } from "@/components/calculators/CalcIcon";

export const Route = createFileRoute("/pt-br/calculators/")({
  head: () => ({
    meta: [
      { title: "Calculadoras Online Grátis — 122+ Ferramentas de Matemática e Finanças | Picamini.online" },
      {
        name: "description",
        content:
          "Calculadoras online grátis para estudantes, professores e finanças. Calculadora de porcentagem, financiamento imobiliário, IMC, médias e matemática 100% privadas no navegador.",
      },
      {
        name: "keywords",
        content:
          "calculadoras online, calculadora de porcentagem, calculadora de financiamento, calculadora de imc, calculadora de medias, ferramentas de matematica gratis",
      },
      { property: "og:site_name", content: "Picamini.online" },
      { property: "og:title", content: "Calculadoras Online Grátis — 122+ Ferramentas Instantâneas" },
      {
        property: "og:description",
        content:
          "Calculadoras online rápidas, precisas e 100% privadas. Faça cálculos matemáticos, financeiros e de saúde direto no seu navegador.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://picamini.online/pt-br/calculators" },
      { property: "og:image", content: "https://picamini.online/og-image.png" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:locale", content: "pt_BR" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Calculadoras Online Grátis — 122+ Ferramentas" },
      { name: "twitter:description", content: "Calculadoras online grátis para matemática, finanças, fitness e física." },
      { name: "twitter:image", content: "https://picamini.online/og-image.png" },
      { name: "theme-color", content: "#4F46E5" },
      { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" },
    ],
    links: [
      { rel: "canonical", href: "https://picamini.online/pt-br/calculators" },
      { rel: "alternate", hrefLang: "en", href: "https://picamini.online/calculators" },
      { rel: "alternate", hrefLang: "es", href: "https://picamini.online/es/calculators" },
      { rel: "alternate", hrefLang: "de", href: "https://picamini.online/de/calculators" },
      { rel: "alternate", hrefLang: "fr", href: "https://picamini.online/fr/calculators" },
      { rel: "alternate", hrefLang: "pt-BR", href: "https://picamini.online/pt-br/calculators" },
      { rel: "alternate", hrefLang: "x-default", href: "https://picamini.online/calculators" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "name": "Diretório de Calculadoras Online Grátis",
          "url": "https://picamini.online/pt-br/calculators",
          "inLanguage": "pt-BR",
          "description": "Diretório de 122+ calculadoras online gratuitas e privadas no navegador para matemática, finanças e saúde.",
          "mainEntity": {
            "@type": "ItemList",
            "numberOfItems": PT_BR_CALCULATORS.length,
            "itemListElement": PT_BR_CALCULATORS.slice(0, 20).map((c, index) => ({
              "@type": "ListItem",
              "position": index + 1,
              "name": c.name,
              "url": `https://picamini.online/pt-br/calculators/${c.slug}`,
              "description": c.tagline
            }))
          }
        })
      }
    ]
  }),
  component: PtBrCalculatorsHub,
});

export function PtBrCalculatorsHub() {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState<PtBrCalculatorCategory | "Todas">("Todas");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return PT_BR_CALCULATORS.filter((c) => {
      const matchesCategory = category === "Todas" || c.category === category;
      const matchesQuery =
        !q ||
        c.name.toLowerCase().includes(q) ||
        c.tagline.toLowerCase().includes(q) ||
        c.keywords.some((k) => k.includes(q));
      return matchesCategory && matchesQuery;
    });
  }, [query, category]);

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader lang="pt-BR" />
      <main>
        {/* Hero Section */}
        <section className="bg-hero-gradient text-primary-foreground">
          <div className="mx-auto max-w-[1480px] px-4 sm:px-6 lg:px-8 py-16 sm:py-20 text-center">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3.5 py-1 text-xs font-bold uppercase tracking-wider backdrop-blur mb-4">
              <ShieldCheck className="size-4 text-emerald-300" />
              100% Grátis & Privado — Cálculos Instantâneos no Navegador
            </div>
            <h1 className="text-3xl font-black tracking-tight sm:text-5xl lg:text-6xl">
              Calculadoras Online Grátis
            </h1>
            <p className="mx-auto mt-4 max-w-2xl text-base sm:text-lg text-primary-foreground/80 leading-relaxed">
              Ferramentas rápidas, precisas e fáceis de usar para estudantes, professores e finanças diárias.
              Calcule porcentagens, notas escolares, juros compostos, IMC e muito mais.
            </p>

            {/* Live Search Bar */}
            <div className="mx-auto mt-8 max-w-xl">
              <div className="relative flex items-center">
                <Search className="absolute left-4 size-5 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Buscar calculadora (ex. porcentagem, notas, juros)..."
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="h-13 pl-12 pr-4 rounded-2xl bg-background/95 text-foreground shadow-lg backdrop-blur text-sm sm:text-base border-white/20 focus-visible:ring-2 focus-visible:ring-primary"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Catalog Section */}
        <section className="mx-auto max-w-[1480px] px-4 sm:px-6 lg:px-8 py-12">
          {/* Category Tabs */}
          <div className="flex flex-wrap items-center gap-2 border-b border-border/80 pb-4">
            <Button
              variant={category === "Todas" ? "default" : "outline"}
              size="sm"
              onClick={() => setCategory("Todas")}
              className="rounded-full text-xs font-bold"
            >
              Todas ({PT_BR_CALCULATORS.length})
            </Button>
            {PT_BR_CALCULATOR_CATEGORIES.map((cat) => (
              <Button
                key={cat}
                variant={category === cat ? "default" : "outline"}
                size="sm"
                onClick={() => setCategory(cat)}
                className="rounded-full text-xs font-bold"
              >
                {cat}
              </Button>
            ))}
          </div>

          {/* Calculator Grid */}
          <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((calc) => (
              <Link
                key={calc.slug}
                to={`/pt-br/calculators/${calc.slug}`}
                className="group relative flex flex-col justify-between rounded-3xl border border-border/80 bg-card p-6 shadow-xs transition-all hover:-translate-y-1 hover:border-primary/50 hover:shadow-xl"
              >
                <div>
                  <div className="flex items-center justify-between">
                    <div className="grid size-12 place-items-center rounded-2xl bg-primary/10 text-primary transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                      <CalcIcon name={calc.icon} className="size-6" />
                    </div>
                    {calc.featured && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/10 px-2.5 py-0.5 text-[11px] font-extrabold uppercase tracking-wider text-amber-600 dark:text-amber-400 border border-amber-500/20">
                        <Sparkles className="size-3" /> Destaque
                      </span>
                    )}
                  </div>

                  <h3 className="mt-5 text-xl font-bold text-foreground transition-colors group-hover:text-primary">
                    {calc.name}
                  </h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                    {calc.tagline}
                  </p>
                </div>

                <div className="mt-6 flex items-center justify-between border-t border-border/60 pt-4 text-xs font-bold text-primary">
                  <span>Abrir calculadora</span>
                  <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                </div>
              </Link>
            ))}
          </div>

          {filtered.length === 0 && (
            <div className="py-16 text-center">
              <p className="text-lg font-bold text-foreground">Nenhuma calculadora encontrada</p>
              <p className="text-sm text-muted-foreground mt-1">Tente pesquisar por outros termos ou selecione outra categoria.</p>
            </div>
          )}
        </section>

        {/* Informational Section */}
        <section className="border-t border-border/60 bg-secondary/15 py-16">
          <div className="mx-auto max-w-5xl px-4 sm:px-6">
            <article className="prose prose-slate dark:prose-invert max-w-none space-y-12">
              <header className="space-y-3">
                <span className="text-xs font-bold uppercase tracking-widest text-primary">
                  Ferramentas Precisas, Gratuitas e Seguras
                </span>
                <h2 className="font-display text-3xl font-extrabold tracking-tight sm:text-4xl text-foreground">
                  A Coleção Completa de Calculadoras Online
                </h2>
                <p className="text-base text-muted-foreground leading-relaxed">
                  O Picamini.online disponibiliza <strong>122+ calculadoras gratuitas</strong> para resolver equações matemáticas, simular parcelas de financiamento, acompanhar saúde e notas escolares com total privacidade no navegador.
                </p>
              </header>

              <div className="grid gap-6 sm:grid-cols-3 not-prose">
                <div className="rounded-2xl border border-border/80 bg-card p-6 shadow-xs">
                  <div className="flex size-10 items-center justify-center rounded-xl bg-primary/10 text-primary mb-4">
                    <ShieldCheck className="size-5" />
                  </div>
                  <h3 className="text-lg font-bold text-foreground">100% Privado</h3>
                  <p className="mt-2 text-sm text-muted-foreground">Todos os cálculos rodam localmente no seu navegador sem envio para servidores.</p>
                </div>
                <div className="rounded-2xl border border-border/80 bg-card p-6 shadow-xs">
                  <div className="flex size-10 items-center justify-center rounded-xl bg-primary/10 text-primary mb-4">
                    <Zap className="size-5" />
                  </div>
                  <h3 className="text-lg font-bold text-foreground">Resultados em Tempo Real</h3>
                  <p className="mt-2 text-sm text-muted-foreground">Atualização instantânea conforme você digita, com fórmulas e passo a passo.</p>
                </div>
                <div className="rounded-2xl border border-border/80 bg-card p-6 shadow-xs">
                  <div className="flex size-10 items-center justify-center rounded-xl bg-primary/10 text-primary mb-4">
                    <CheckCircle2 className="size-5" />
                  </div>
                  <h3 className="text-lg font-bold text-foreground">Sem Cadastro</h3>
                  <p className="mt-2 text-sm text-muted-foreground">Acesso ilimitado e livre sem necessidade de conta ou assinatura.</p>
                </div>
              </div>
            </article>
          </div>
        </section>
      </main>
      <SiteFooter lang="pt-BR" />
    </div>
  );
}
